import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import api, { usersAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { 
  Users, UserCircle, Mail, Phone, Briefcase, Clock, CheckSquare,
  Trophy, TrendingUp, Edit2, Save, X, Plus, BarChart3,
  AlertCircle, CheckCircle, Coffee, Palmtree, Activity, UserPlus, Key,
  Shield, Eye, Pencil, Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Switch } from '../components/ui/switch';

const AVAILABILITY_STATUS = {
  available: { label: 'Disponibil', color: 'bg-emerald-500', icon: CheckCircle },
  busy: { label: 'Ocupat', color: 'bg-amber-500', icon: Activity },
  vacation: { label: 'Concediu', color: 'bg-blue-500', icon: Palmtree },
  sick: { label: 'Medical', color: 'bg-red-500', icon: AlertCircle }
};

const WORKLOAD_COLORS = {
  low: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  normal: 'bg-blue-100 text-blue-700 border-blue-200',
  high: 'bg-amber-100 text-amber-700 border-amber-200',
  overloaded: 'bg-red-100 text-red-700 border-red-200'
};

const WorkloadBar = ({ percentage, status }) => {
  const color = status === 'overloaded' ? 'bg-red-500' : 
                status === 'high' ? 'bg-amber-500' : 
                status === 'normal' ? 'bg-blue-500' : 'bg-emerald-500';
  
  return (
    <div className="w-full">
      <div className="flex justify-between text-xs mb-1">
        <span className="text-muted-foreground">Workload</span>
        <span className="font-medium">{percentage}%</span>
      </div>
      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} transition-all duration-500`} 
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
    </div>
  );
};

const SkillTag = ({ skill, onRemove, editable = false }) => (
  <div className="inline-flex items-center gap-1 px-2 py-1 bg-slate-100 rounded-full text-sm">
    <span>{skill}</span>
    {editable && onRemove && (
      <button onClick={() => onRemove(skill)} className="hover:text-red-500">
        <X className="w-3 h-3" />
      </button>
    )}
  </div>
);

const TeamMemberCard = ({ member, onViewProfile, onEditUser, isAdmin }) => {
  const status = AVAILABILITY_STATUS[member.availability_status] || AVAILABILITY_STATUS.available;
  const StatusIcon = status.icon;
  
  return (
    <Card 
      className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-[1.02] relative group"
      onClick={() => onViewProfile(member.user_id)}
      data-testid={`team-member-${member.user_id}`}
    >
      {isAdmin && (
        <Button
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10"
          onClick={(e) => onEditUser(member, e)}
          title="Editează permisiuni"
        >
          <Shield className="w-4 h-4 text-blue-500" />
        </Button>
      )}
      <CardContent className="p-4">
        <div className="flex items-start gap-4">
          <div className="relative">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white text-xl font-bold">
              {member.full_name?.charAt(0).toUpperCase()}
            </div>
            <div className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full ${status.color} border-2 border-white flex items-center justify-center`}>
              <StatusIcon className="w-3 h-3 text-white" />
            </div>
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold truncate">{member.full_name}</h3>
            <p className="text-sm text-muted-foreground truncate">{member.email}</p>
            <Badge variant="secondary" className="mt-1 text-xs">
              {member.role === 'admin' ? 'Admin' : member.role === 'project_manager' ? 'PM' : 'Team Member'}
            </Badge>
          </div>
        </div>
        
        <div className="mt-4">
          <WorkloadBar percentage={member.workload_pct} status={member.workload_pct > 100 ? 'overloaded' : member.workload_pct > 80 ? 'high' : member.workload_pct > 40 ? 'normal' : 'low'} />
        </div>
        
        <div className="mt-3 flex items-center justify-between text-sm">
          <div className="flex items-center gap-1 text-muted-foreground">
            <CheckSquare className="w-4 h-4" />
            <span>{member.active_tasks} taskuri</span>
          </div>
          <div className="flex items-center gap-1 text-muted-foreground">
            <Clock className="w-4 h-4" />
            <span>{member.hours_this_month}h</span>
          </div>
        </div>
        
        {member.skills?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {member.skills.slice(0, 3).map((skill, idx) => (
              <span key={idx} className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">
                {skill}
              </span>
            ))}
            {member.skills.length > 3 && (
              <span className="px-2 py-0.5 text-xs text-slate-400">
                +{member.skills.length - 3}
              </span>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const ProfileDialog = ({ userId, isOpen, onClose }) => {
  const { user: currentUser } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [newSkill, setNewSkill] = useState('');

  useEffect(() => {
    if (isOpen && userId) {
      fetchProfile();
    }
  }, [isOpen, userId]);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/users/${userId}/profile`);
      setProfile(response.data);
      setEditData({
        bio: response.data.profile?.bio || '',
        phone: response.data.profile?.phone || '',
        department: response.data.profile?.department || '',
        position: response.data.profile?.position || '',
        availability_status: response.data.profile?.availability_status || 'available',
        skills: response.data.profile?.skills || []
      });
    } catch (err) {
      toast.error('Eroare la încărcarea profilului');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      await api.put(`/users/${userId}/profile`, editData);
      toast.success('Profil actualizat!');
      setEditing(false);
      fetchProfile();
    } catch (err) {
      toast.error('Eroare la salvarea profilului');
    }
  };

  const addSkill = () => {
    if (newSkill && !editData.skills.includes(newSkill)) {
      setEditData({ ...editData, skills: [...editData.skills, newSkill] });
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setEditData({ ...editData, skills: editData.skills.filter(s => s !== skill) });
  };

  const canEdit = currentUser?.id === userId || currentUser?.role === 'admin';

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Profil Utilizator</span>
            {canEdit && !editing && (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                <Edit2 className="w-4 h-4 mr-1" /> Editează
              </Button>
            )}
          </DialogTitle>
        </DialogHeader>

        {profile && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white text-3xl font-bold">
                {profile.full_name?.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1">
                <h2 className="text-2xl font-bold">{profile.full_name}</h2>
                <p className="text-muted-foreground">{profile.email}</p>
                <div className="flex items-center gap-2 mt-2">
                  <Badge>{profile.role === 'admin' ? 'Administrator' : profile.role === 'project_manager' ? 'Project Manager' : 'Team Member'}</Badge>
                  {editing ? (
                    <Select value={editData.availability_status} onValueChange={(v) => setEditData({...editData, availability_status: v})}>
                      <SelectTrigger className="w-36">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available">Disponibil</SelectItem>
                        <SelectItem value="busy">Ocupat</SelectItem>
                        <SelectItem value="vacation">Concediu</SelectItem>
                        <SelectItem value="sick">Medical</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (
                    <Badge className={AVAILABILITY_STATUS[profile.profile?.availability_status]?.color || 'bg-emerald-500'}>
                      {AVAILABILITY_STATUS[profile.profile?.availability_status]?.label || 'Disponibil'}
                    </Badge>
                  )}
                </div>
              </div>
            </div>

            {/* Workload Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4 text-center">
                  <CheckSquare className="w-6 h-6 mx-auto mb-1 text-blue-500" />
                  <p className="text-2xl font-bold">{profile.workload?.active_tasks}</p>
                  <p className="text-xs text-muted-foreground">Taskuri Active</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Briefcase className="w-6 h-6 mx-auto mb-1 text-purple-500" />
                  <p className="text-2xl font-bold">{profile.workload?.active_projects}</p>
                  <p className="text-xs text-muted-foreground">Proiecte</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <Clock className="w-6 h-6 mx-auto mb-1 text-orange-500" />
                  <p className="text-2xl font-bold">{profile.workload?.hours_this_month}h</p>
                  <p className="text-xs text-muted-foreground">Ore Luna Asta</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 text-center">
                  <BarChart3 className="w-6 h-6 mx-auto mb-1 text-emerald-500" />
                  <p className="text-2xl font-bold">{profile.workload?.workload_pct}%</p>
                  <p className="text-xs text-muted-foreground">Workload</p>
                </CardContent>
              </Card>
            </div>

            {/* Bio & Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium mb-2 block">Bio</label>
                {editing ? (
                  <Input 
                    value={editData.bio} 
                    onChange={(e) => setEditData({...editData, bio: e.target.value})}
                    placeholder="Scurtă descriere..."
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">{profile.profile?.bio || 'Nicio descriere'}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Departament</label>
                {editing ? (
                  <Input 
                    value={editData.department} 
                    onChange={(e) => setEditData({...editData, department: e.target.value})}
                    placeholder="ex: Dezvoltare"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">{profile.profile?.department || 'Nespecificat'}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Poziție</label>
                {editing ? (
                  <Input 
                    value={editData.position} 
                    onChange={(e) => setEditData({...editData, position: e.target.value})}
                    placeholder="ex: Senior Developer"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">{profile.profile?.position || 'Nespecificat'}</p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Telefon</label>
                {editing ? (
                  <Input 
                    value={editData.phone} 
                    onChange={(e) => setEditData({...editData, phone: e.target.value})}
                    placeholder="ex: +40 7XX XXX XXX"
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">{profile.profile?.phone || 'Nespecificat'}</p>
                )}
              </div>
            </div>

            {/* Skills */}
            <div>
              <label className="text-sm font-medium mb-2 block">Competențe</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {(editing ? editData.skills : profile.profile?.skills || []).map((skill, idx) => (
                  <SkillTag key={idx} skill={skill} editable={editing} onRemove={removeSkill} />
                ))}
                {(!editing && (!profile.profile?.skills || profile.profile.skills.length === 0)) && (
                  <p className="text-sm text-muted-foreground">Nicio competență adăugată</p>
                )}
              </div>
              {editing && (
                <div className="flex gap-2">
                  <Input 
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    placeholder="Adaugă competență..."
                    onKeyDown={(e) => e.key === 'Enter' && addSkill()}
                  />
                  <Button onClick={addSkill} size="sm">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>

            {/* Badges */}
            {profile.recent_badges?.length > 0 && (
              <div>
                <label className="text-sm font-medium mb-2 block">Badge-uri Recente</label>
                <div className="flex flex-wrap gap-2">
                  {profile.recent_badges.map((badge, idx) => (
                    <div key={idx} className="flex items-center gap-1 px-3 py-1 bg-amber-50 rounded-full text-amber-700 border border-amber-200">
                      <Trophy className="w-4 h-4" />
                      <span className="text-sm font-medium">{badge.name_ro || badge.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Actions */}
            {editing && (
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditing(false)}>
                  Anulează
                </Button>
                <Button onClick={handleSave}>
                  <Save className="w-4 h-4 mr-1" /> Salvează
                </Button>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const TeamWorkloadOverview = ({ workload }) => {
  if (!workload) return null;

  return (
    <Card className="mb-6">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-500" />
          Sumar Workload Echipă
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-3xl font-bold">{workload.team_size}</p>
            <p className="text-sm text-muted-foreground">Membri</p>
          </div>
          <div>
            <p className="text-3xl font-bold">{workload.avg_workload}%</p>
            <p className="text-sm text-muted-foreground">Workload Mediu</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-emerald-600">
              {workload.members?.filter(m => m.availability_status === 'available').length || 0}
            </p>
            <p className="text-sm text-muted-foreground">Disponibili</p>
          </div>
          <div>
            <p className="text-3xl font-bold text-red-600">
              {workload.members?.filter(m => m.workload_pct > 80).length || 0}
            </p>
            <p className="text-sm text-muted-foreground">Supraîncărcați</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const AddUserDialog = ({ isOpen, onClose, onUserAdded }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'team_member'
  });
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password });
    setShowPassword(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      await api.post('/auth/register', formData);
      toast.success(`Utilizatorul ${formData.full_name} a fost creat!`);
      setFormData({ email: '', password: '', full_name: '', role: 'team_member' });
      onUserAdded();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la crearea utilizatorului');
    } finally {
      setSaving(false);
    }
  };

  const roleLabels = {
    admin: 'Administrator',
    project_manager: 'Project Manager',
    team_member: 'Team Member'
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-blue-500" />
            Adaugă Utilizator Nou
          </DialogTitle>
          <DialogDescription>
            Creează un cont nou pentru un membru al echipei
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="full_name">Nume complet *</Label>
            <Input
              id="full_name"
              value={formData.full_name}
              onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              placeholder="ex: Ion Popescu"
              required
              data-testid="new-user-name"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="ex: ion@adsem.ro"
              required
              data-testid="new-user-email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">Parolă *</Label>
            <div className="flex gap-2">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Introduceți parola"
                required
                data-testid="new-user-password"
              />
              <Button 
                type="button" 
                variant="outline" 
                size="icon"
                onClick={generatePassword}
                title="Generează parolă"
              >
                <Key className="w-4 h-4" />
              </Button>
            </div>
            {showPassword && formData.password && (
              <p className="text-xs text-muted-foreground bg-slate-100 p-2 rounded font-mono">
                Parola: {formData.password}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">Rol în companie *</Label>
            <Select 
              value={formData.role} 
              onValueChange={(v) => setFormData({ ...formData, role: v })}
            >
              <SelectTrigger data-testid="new-user-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="team_member">Team Member</SelectItem>
                <SelectItem value="project_manager">Project Manager</SelectItem>
                <SelectItem value="admin">Administrator</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Anulează
            </Button>
            <Button 
              type="submit" 
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="create-user-btn"
            >
              {saving ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Se creează...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4" />
                  Creează Utilizator
                </span>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Permission categories with labels
const PERMISSION_CATEGORIES = [
  { key: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { key: 'projects', label: 'Proiecte', icon: Briefcase },
  { key: 'tasks', label: 'Taskuri', icon: CheckSquare },
  { key: 'time_tracking', label: 'Time Tracking', icon: Clock },
  { key: 'team', label: 'Echipa', icon: Users },
  { key: 'reports', label: 'Rapoarte', icon: BarChart3 },
  { key: 'client_profitability', label: 'Profitabilitate Clienți', icon: TrendingUp },
  { key: 'performance', label: 'Performanță', icon: Trophy },
  { key: 'concedii', label: 'Concedii', icon: Palmtree },
  { key: 'capacity_planning', label: 'Capacity Planning', icon: Activity },
  { key: 'configurare_alerte', label: 'Configurare Alerte', icon: AlertCircle },
  { key: 'salarii', label: 'Salarii', icon: Briefcase },
  { key: 'evaluation', label: 'Evaluare Clienți', icon: CheckCircle }
];

const EditUserDialog = ({ user: editingUser, isOpen, onClose, onUserUpdated }) => {
  const [formData, setFormData] = useState({
    full_name: '',
    role: 'team_member',
    hourly_cost_eur: 0,
    is_active: true,
    permissions: {},
    ai_access_enabled: false,
    ai_permissions: { can_use_chat: true, can_create_tasks: true, can_create_projects: false }
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (editingUser) {
      setFormData({
        full_name: editingUser.full_name || '',
        role: editingUser.role || 'team_member',
        hourly_cost_eur: editingUser.hourly_cost_eur || 0,
        is_active: editingUser.is_active !== false,
        permissions: editingUser.permissions || getDefaultPermissions(editingUser.role),
        ai_access_enabled: editingUser.ai_access_enabled || false,
        ai_permissions: editingUser.ai_permissions || { can_use_chat: true, can_create_tasks: true, can_create_projects: false }
      });
    }
  }, [editingUser]);

  const getDefaultPermissions = (role) => {
    if (role === 'admin') {
      return PERMISSION_CATEGORIES.reduce((acc, cat) => ({ ...acc, [cat.key]: 'edit' }), {});
    }
    return {
      dashboard: 'view',
      projects: 'view',
      tasks: 'edit',
      time_tracking: 'edit',
      team: 'none',
      reports: 'view',
      client_profitability: 'none',
      performance: 'view',
      concedii: 'edit',
      capacity_planning: 'none',
      configurare_alerte: 'none',
      salarii: 'none',
      evaluation: 'view'
    };
  };

  const handlePermissionChange = (category, value) => {
    setFormData(prev => ({
      ...prev,
      permissions: { ...prev.permissions, [category]: value }
    }));
  };
  
  const handleAIPermissionChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      ai_permissions: { ...prev.ai_permissions, [key]: value }
    }));
  };

  const handleRoleChange = (newRole) => {
    setFormData(prev => ({
      ...prev,
      role: newRole,
      permissions: getDefaultPermissions(newRole)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    const userId = editingUser.user_id || editingUser.id;
    console.log('Updating user:', userId, 'with data:', formData);
    
    try {
      const response = await api.put(`/users/${userId}`, formData);
      console.log('Update response:', response.data);
      toast.success('Utilizatorul a fost actualizat!');
      onUserUpdated();
      onClose();
    } catch (err) {
      console.error('Update error:', err.response?.status, err.response?.data);
      toast.error(err.response?.data?.detail || 'Eroare la actualizarea utilizatorului');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm(`Sigur vrei să ștergi utilizatorul ${editingUser?.full_name}?`)) return;
    
    setDeleting(true);
    try {
      await api.delete(`/users/${editingUser.user_id || editingUser.id}`);
      toast.success('Utilizatorul a fost șters!');
      onUserUpdated();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Eroare la ștergerea utilizatorului');
    } finally {
      setDeleting(false);
    }
  };

  if (!editingUser) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-500" />
            Editare Utilizator & Permisiuni
          </DialogTitle>
          <DialogDescription>
            Modifică datele și nivelul de acces pentru {editingUser.full_name}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nume complet</Label>
              <Input
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select value={formData.role} onValueChange={handleRoleChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="team_member">Team Member</SelectItem>
                  <SelectItem value="project_manager">Project Manager</SelectItem>
                  <SelectItem value="admin">Administrator</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Cost orar (EUR)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.hourly_cost_eur}
                onChange={(e) => setFormData({ ...formData, hourly_cost_eur: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>Status cont</Label>
              <div className="flex items-center gap-3 pt-2">
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
                <span className={formData.is_active ? 'text-green-600' : 'text-red-600'}>
                  {formData.is_active ? 'Activ' : 'Dezactivat'}
                </span>
              </div>
            </div>
          </div>

          {/* Permissions */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b">
              <Shield className="w-5 h-5 text-slate-500" />
              <Label className="text-base font-semibold">Permisiuni Acces</Label>
            </div>
            
            <div className="grid grid-cols-1 gap-2">
              {PERMISSION_CATEGORIES.map(({ key, label, icon: Icon }) => (
                <div key={key} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon className="w-4 h-4 text-slate-500" />
                    <span className="font-medium">{label}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant={formData.permissions[key] === 'none' ? 'default' : 'outline'}
                      className={formData.permissions[key] === 'none' ? 'bg-red-500 hover:bg-red-600' : ''}
                      onClick={() => handlePermissionChange(key, 'none')}
                    >
                      <X className="w-3 h-3 mr-1" />
                      Blocat
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={formData.permissions[key] === 'view' ? 'default' : 'outline'}
                      className={formData.permissions[key] === 'view' ? 'bg-blue-500 hover:bg-blue-600' : ''}
                      onClick={() => handlePermissionChange(key, 'view')}
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      View
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={formData.permissions[key] === 'edit' ? 'default' : 'outline'}
                      className={formData.permissions[key] === 'edit' ? 'bg-green-500 hover:bg-green-600' : ''}
                      onClick={() => handlePermissionChange(key, 'edit')}
                    >
                      <Pencil className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AI Access Permissions */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 pb-2 border-b">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-500">
                <path d="M12 8V4H8"/>
                <rect width="16" height="12" x="4" y="8" rx="2"/>
                <path d="M2 14h2"/>
                <path d="M20 14h2"/>
                <path d="M15 13v2"/>
                <path d="M9 13v2"/>
              </svg>
              <Label className="text-base font-semibold">Acces AI Assistant</Label>
            </div>
            
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <Label className="text-base">Activează Acces AI</Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    Permite acestui utilizator să folosească AI Admin Assistant
                  </p>
                </div>
                <Switch
                  checked={formData.ai_access_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, ai_access_enabled: checked })}
                />
              </div>
              
              {formData.ai_access_enabled && (
                <div className="space-y-3 pt-3 border-t border-purple-200">
                  <Label className="text-sm font-medium">Funcții permise:</Label>
                  
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm">Chat cu AI</span>
                    <Switch
                      checked={formData.ai_permissions?.can_use_chat ?? true}
                      onCheckedChange={(checked) => handleAIPermissionChange('can_use_chat', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm">Creare Task-uri prin AI</span>
                    <Switch
                      checked={formData.ai_permissions?.can_create_tasks ?? true}
                      onCheckedChange={(checked) => handleAIPermissionChange('can_create_tasks', checked)}
                    />
                  </div>
                  
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm">Creare Proiecte prin AI</span>
                    <Switch
                      checked={formData.ai_permissions?.can_create_projects ?? false}
                      onCheckedChange={(checked) => handleAIPermissionChange('can_create_projects', checked)}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between pt-4 border-t">
            <Button 
              type="button" 
              variant="destructive" 
              onClick={handleDelete}
              disabled={deleting}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {deleting ? 'Se șterge...' : 'Șterge Utilizator'}
            </Button>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>
                Anulează
              </Button>
              <Button type="submit" disabled={saving}>
                <Save className="w-4 h-4 mr-1" />
                {saving ? 'Se salvează...' : 'Salvează'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export const Team = () => {
  const { user } = useAuth();
  const [teamWorkload, setTeamWorkload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [filter, setFilter] = useState('all');

  const isAdmin = user?.role === 'admin' || user?.role === 'project_manager';

  useEffect(() => {
    fetchTeamData();
  }, []);

  const fetchTeamData = async () => {
    setLoading(true);
    try {
      if (isAdmin) {
        const response = await api.get('/users/team-workload');
        setTeamWorkload(response.data);
      } else {
        const response = await usersAPI.getAll();
        setTeamWorkload({
          team_size: response.data.length,
          avg_workload: 0,
          members: response.data.map(u => ({
            user_id: u.id,
            id: u.id,
            full_name: u.full_name,
            email: u.email,
            role: u.role,
            permissions: u.permissions,
            hourly_cost_eur: u.hourly_cost_eur,
            is_active: u.is_active,
            ai_access_enabled: u.ai_access_enabled,
            ai_permissions: u.ai_permissions,
            active_tasks: 0,
            hours_this_month: 0,
            workload_pct: 0,
            availability_status: 'available',
            skills: []
          }))
        });
      }
    } catch (err) {
      toast.error('Eroare la încărcarea datelor echipei');
    } finally {
      setLoading(false);
    }
  };

  const openProfile = (userId) => {
    setSelectedUserId(userId);
    setProfileOpen(true);
  };

  const openEditUser = (member, e) => {
    e.stopPropagation();
    setEditingMember(member);
    setEditUserOpen(true);
  };

  const filteredMembers = teamWorkload?.members?.filter(m => {
    if (filter === 'all') return true;
    if (filter === 'available') return m.availability_status === 'available';
    if (filter === 'overloaded') return m.workload_pct > 80;
    if (filter === 'admin') return m.role === 'admin';
    if (filter === 'pm') return m.role === 'project_manager';
    if (filter === 'member') return m.role === 'team_member';
    return true;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Users className="w-7 h-7 text-blue-500" />
              Echipa
            </h1>
            <p className="text-muted-foreground">Gestionează membrii echipei și workload-ul</p>
          </div>
          <div className="flex items-center gap-3">
            {user?.role === 'admin' && (
              <Button 
                onClick={() => setAddUserOpen(true)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="add-user-btn"
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Adaugă Utilizator
              </Button>
            )}
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filtrează..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toți membrii</SelectItem>
                <SelectItem value="available">Disponibili</SelectItem>
                <SelectItem value="overloaded">Supraîncărcați</SelectItem>
                <SelectItem value="admin">Admini</SelectItem>
                <SelectItem value="pm">Project Managers</SelectItem>
                <SelectItem value="member">Team Members</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {isAdmin && <TeamWorkloadOverview workload={teamWorkload} />}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMembers?.map((member) => (
            <TeamMemberCard 
              key={member.user_id} 
              member={member} 
              onViewProfile={openProfile}
              onEditUser={openEditUser}
              isAdmin={user?.role === 'admin'}
            />
          ))}
        </div>

        {filteredMembers?.length === 0 && (
          <Card>
            <CardContent className="text-center py-12">
              <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-30" />
              <p className="text-muted-foreground">Niciun membru găsit cu acest filtru</p>
            </CardContent>
          </Card>
        )}

        <ProfileDialog 
          userId={selectedUserId}
          isOpen={profileOpen}
          onClose={() => setProfileOpen(false)}
        />

        <AddUserDialog
          isOpen={addUserOpen}
          onClose={() => setAddUserOpen(false)}
          onUserAdded={fetchTeamData}
        />

        <EditUserDialog
          user={editingMember}
          isOpen={editUserOpen}
          onClose={() => setEditUserOpen(false)}
          onUserUpdated={fetchTeamData}
        />
      </div>
    </Layout>
  );
};
